/* globals svgEditor */
import {
  marchingSquaresSquares,
  simplifyContour,
  offsetClosedPath,
  pathToD
} from '../utils/sticker-outline.js'
import { TransparencyMask } from '../utils/transparency-mask.js'
import stickerDialogHTML from './stickerDialog.html'
import { allCapsShadowCss } from '../components/allCapsShadowCss.js'

const template = document.createElement('template')
template.innerHTML = allCapsShadowCss + stickerDialogHTML
const MM_TO_PX = 96 / 25.4

/** @typedef {'bitmap'|'image'} StickerSource */

// Consumers can reject confirmation with preventDefault to keep failed edits local.
export class SeStickerDialog extends HTMLElement {
  constructor () {
    super()
    this._shadowRoot = this.attachShadow({ mode: 'open' })
    this._shadowRoot.append(template.content.cloneNode(true))
    const find = (id) => this._shadowRoot.querySelector(`#sticker_${id}`)
    this.$dialog = find('box')
    this.$okBtn = find('ok')
    this.$cancelBtn = find('cancel')
    this.$hint = find('hint')
    this.$preview = find('preview')
    this.$threshold = find('threshold')
    this.$thresholdValue = find('threshold_value')
    this.$offset = find('offset')
    this.$unit = find('unit')
    this.$eraseEnable = find('erase_enable')
    this.$editControls = find('edit_controls')
    this.$tool = find('tool')
    this.$brushWrap = find('brush_wrap')
    this.$brush = find('brush')
    this.$brushValue = find('brush_value')
    this.$wandWrap = find('wand_wrap')
    this.$colorTolerance = find('color_tolerance')
    this.$colorToleranceValue = find('color_tolerance_value')
    this.$contiguous = find('contiguous')
    this.$undo = find('undo')
    this.$redo = find('redo')
    this.$eraseReset = find('erase_reset')
    this.$note = find('note')
    this.$error = find('error')
    this.$tolerance = find('tolerance')
    this.$toleranceValue = find('tolerance_value')
    this.$eraserCursor = document.createElement('div')
    this.$eraserCursor.id = 'sticker_eraser_cursor'
    this.$preview.parentElement.appendChild(this.$eraserCursor)

    this._image = null
    this._source = 'bitmap'
    this._sourceElement = null
    this._model = null
    // Retained for the existing preview/eraser integrations; aliases model.mask.
    this._eraseMask = null
    this._erasePainting = false
    this._loadId = 0
    this._updateTimer = null
    this._rAF = 0
    this._d = ''

    this.$threshold.addEventListener('input', () => {
      this.$thresholdValue.textContent = this.$threshold.value
      this._scheduleUpdate()
    })
    this.$offset.addEventListener('input', () => this._scheduleUpdate())
    this.$tolerance.addEventListener('input', () => {
      this.$toleranceValue.textContent = this.$tolerance.value
      this._scheduleUpdate()
    })
    this.$unit.addEventListener('change', () => this._scheduleUpdate())
    this.$eraseEnable.addEventListener('change', () => this._toolChanged())
    this.$tool.addEventListener('change', () => this._toolChanged())
    this.$brush.addEventListener('input', () => {
      this.$brushValue.textContent = this.$brush.value
    })
    this.$colorTolerance.addEventListener('input', () => {
      this.$colorToleranceValue.textContent = this.$colorTolerance.value
    })
    for (const [button, action] of [[this.$undo, 'undo'], [this.$redo, 'redo'], [this.$eraseReset, 'reset']]) {
      button.addEventListener('click', () => {
        this._finishStroke()
        this._model?.[action]()
        this._update()
      })
    }
    this.$preview.addEventListener('pointerdown', (e) => {
      if (!this.$eraseEnable.checked || !this._model || e.button !== 0 || this._erasePainting) return
      e.preventDefault()
      const { x, y } = this._toImageCoords(e)
      if (this.$tool.value === 'wand') {
        this._model.wand(x, y, Number(this.$colorTolerance.value), this.$contiguous.checked)
        this._pixelsChanged()
      } else {
        this._erasePainting = true
        this._pointerId = e.pointerId
        this._lastPoint = { x, y }
        this._model.beginStroke()
        this.$preview.setPointerCapture(e.pointerId)
        this._paintErase(x, y)
      }
    })
    this.$preview.addEventListener('pointermove', (e) => {
      if (this._erasePainting && e.pointerId === this._pointerId) {
        const { x, y } = this._toImageCoords(e)
        this._paintErase(x, y)
      }
      this._showCursor(e)
    })
    this.$preview.addEventListener('pointerup', (e) => {
      if (e.pointerId !== this._pointerId) return
      const { x, y } = this._toImageCoords(e)
      this._paintErase(x, y)
      this._finishStroke()
    })
    for (const type of ['pointercancel', 'lostpointercapture']) {
      this.$preview.addEventListener(type, () => this._finishStroke())
    }
    this.$preview.addEventListener('pointerleave', () => {
      this.$eraserCursor.style.display = 'none'
    })
    this.$dialog.addEventListener('close', () => {
      this._finishStroke()
      this._cancelUpdates()
      this._loadId++
      this.$eraserCursor.style.display = 'none'
      // Keep the host attribute in sync for Escape/backdrop close and reopening.
      if (this.dialog !== 'close') this.setAttribute('dialog', 'close')
      this.dispatchEvent(new CustomEvent('change', { detail: { trigger: 'cancel', dialog: 'closed' } }))
    })
    this.$okBtn.addEventListener('click', () => this._onOk())
    this.$cancelBtn.addEventListener('click', () => { this.dialog = 'close' })
    this._syncControls()
  }

  init (i18next) {
    this._i18next = i18next
    this.setAttribute('common-ok', i18next.t('common.ok'))
    this.setAttribute('common-cancel', i18next.t('common.cancel'))
    this.setAttribute('sticker-hint', i18next.t('sticker.hint'))
    this.setAttribute('sticker-threshold_label', i18next.t('sticker.threshold'))
    this.setAttribute('sticker-tolerance_label', i18next.t('sticker.tolerance'))
    this.setAttribute('sticker-offset_label', i18next.t('sticker.offset'))
    this.setAttribute('sticker-unit_px', i18next.t('sticker.unit_px'))
    this.setAttribute('sticker-unit_mm', i18next.t('sticker.unit_mm'))
    this.setAttribute('sticker-erase_label', i18next.t('sticker.erase_enable'))
    this.setAttribute('sticker-brush_label', i18next.t('sticker.brush'))
    this.setAttribute('sticker-erase_reset', i18next.t('sticker.erase_reset'))
    this.setAttribute('sticker-note', i18next.t('sticker.note'))
    this.setAttribute('sticker-no_image', i18next.t('sticker.no_image'))
    this.setAttribute('sticker-no_outline', i18next.t('sticker.no_outline'))
    this.setAttribute('aria-label', i18next.t('tools.sticker_outline'))
    for (const node of this._shadowRoot.querySelectorAll('[data-i18n]')) {
      node.textContent = i18next.t(`sticker.${node.dataset.i18n}`)
    }
    this.$preview.setAttribute('aria-label', i18next.t('sticker.preview'))
  }

  static get observedAttributes () {
    return ['dialog', 'source', 'common-ok', 'common-cancel', 'aria-label',
      'sticker-hint', 'sticker-threshold_label', 'sticker-tolerance_label',
      'sticker-offset_label', 'sticker-unit_px', 'sticker-unit_mm', 'sticker-erase_label',
      'sticker-brush_label', 'sticker-erase_reset', 'sticker-note',
      'sticker-no_image', 'sticker-no_outline']
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === newValue) return
    switch (name) {
      case 'dialog':
        if (newValue === 'open') {
          this._finishStroke()
          this.$error.textContent = ''
          if (this._source === 'image') {
            const canvas = svgEditor.svgCanvas
            const elem = canvas.getSelectedElements()[0]
            this._sourceElement = elem?.nodeName === 'image' ? elem : null
            this._sourceHref = this._sourceElement ? canvas.getHref(this._sourceElement) : null
            this.image = this._sourceHref
          } else {
            this._sourceElement = null
            // A new session always starts with a fresh local mask/history.
            if (this._image) this.image = this._image.src
          }
          this.$dialog.open()
          this._scheduleUpdate()
        } else {
          this._finishStroke()
          this._cancelUpdates()
          this._loadId++
          this.$dialog.close()
        }
        break
      case 'source':
        this._source = newValue === 'image' ? 'image' : 'bitmap'
        break
      case 'common-ok':
        this.$okBtn.textContent = newValue
        break
      case 'common-cancel':
        this.$cancelBtn.textContent = newValue
        break
      case 'sticker-no_image':
        this._msgNoImage = newValue
        break
      case 'sticker-no_outline':
        this._msgNoOutline = newValue
        break
      case 'aria-label':
        this.$dialog.setAttribute('aria-label', newValue)
        break
      default: {
        const node = this._shadowRoot.querySelector(`#${name.replace('sticker-', 'sticker_')}`)
        if (node) node.textContent = newValue
        break
      }
    }
  }

  get image () { return this._image?.src ?? null }

  set image (value) {
    this._finishStroke()
    this._cancelUpdates()
    const loadId = ++this._loadId
    this._image = null
    this._model = null
    this._eraseMask = null
    this._d = ''
    this.$error.textContent = ''
    this.$preview.getContext('2d').clearRect(0, 0, this.$preview.width, this.$preview.height)
    this._syncControls()
    if (!value) {
      this.$hint.textContent = this._msgNoImage ?? ''
      return
    }
    const img = new Image()
    if (!value.startsWith('data:')) img.crossOrigin = 'anonymous'
    img.addEventListener('load', () => {
      if (loadId !== this._loadId) return
      try {
        const cv = this.$preview
        cv.width = img.naturalWidth
        cv.height = img.naturalHeight
        const ctx = cv.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0)
        // Decode/read once. Never sample rendered overlays or the checkerboard.
        this._model = new TransparencyMask(ctx.getImageData(0, 0, cv.width, cv.height).data, cv.width, cv.height)
        this._eraseMask = this._model.mask
        this._image = img
        this._fitPreview()
        this._update()
      } catch (err) {
        this.reportError('read_error', err)
        this._model = null
        this._syncControls()
      }
    })
    img.addEventListener('error', () => {
      if (loadId !== this._loadId) return
      this.reportError('load_error', new Error('Cannot load source image'))
    })
    img.src = value
  }

  _fitPreview () {
    const cv = this.$preview
    const scale = Math.min(460 / cv.width, 260 / cv.height, 1)
    cv.style.width = `${Math.max(1, Math.round(cv.width * scale))}px`
    cv.style.height = `${Math.max(1, Math.round(cv.height * scale))}px`
  }

  get dialog () { return this.getAttribute('dialog') }
  set dialog (value) { this.setAttribute('dialog', value) }

  reportError (key, err) {
    console.error(`sticker: ${key}`, err)
    this.$error.textContent = this._i18next?.t(`sticker.${key}`) ?? `sticker.${key}`
  }

  _cancelUpdates () {
    clearTimeout(this._updateTimer)
    cancelAnimationFrame(this._rAF)
    this._updateTimer = null
    this._rAF = 0
  }

  _scheduleUpdate () {
    clearTimeout(this._updateTimer)
    this._updateTimer = setTimeout(() => this._update(), 120)
  }

  _syncControls () {
    const enabled = this.$eraseEnable.checked
    this.$editControls.hidden = !enabled
    this.$brushWrap.hidden = !enabled || this.$tool.value !== 'eraser'
    this.$wandWrap.hidden = !enabled || this.$tool.value !== 'wand'
    this.$undo.disabled = !this._model?.canUndo
    this.$redo.disabled = !this._model?.canRedo
    this.$eraseReset.disabled = !this._model?.hasErased
    this.$okBtn.disabled = !this._model
  }

  _toolChanged () {
    this._finishStroke()
    this.$eraserCursor.style.display = 'none'
    this._syncControls()
  }

  _pixelsChanged () {
    this._syncControls()
    cancelAnimationFrame(this._rAF)
    this._rAF = requestAnimationFrame(() => {
      try {
        this._render()
      } catch (err) {
        this.reportError('read_error', err)
      }
    })
    this._scheduleUpdate()
  }

  _update () {
    if (!this._model) return false
    try {
      const cv = this.$preview
      const solid = this._model.alphaMask(Number(this.$threshold.value))
      const contours = marchingSquaresSquares(solid, cv.width, cv.height)
      const tolerance = Number(this.$tolerance.value)
      const offsetPx = Number(this.$offset.value) * (this.$unit.value === 'mm' ? MM_TO_PX : 1)
      if (!Number.isFinite(offsetPx)) throw new Error('Invalid outline offset')
      this._d = contours
        .map(c => simplifyContour(c, tolerance, true))
        .filter(c => c.length >= 3)
        .map(c => offsetClosedPath(c, offsetPx))
        .filter(c => c.length >= 3)
        .map(c => pathToD(c, true))
        .join(' ')
      this.$hint.textContent = this._d ? this.getAttribute('sticker-hint') : this._msgNoOutline ?? ''
      this._render()
      this._syncControls()
      return true
    } catch (err) {
      this._d = ''
      this.reportError('read_error', err)
      return false
    }
  }

  _render () {
    const cv = this.$preview
    const ctx = cv.getContext('2d')
    ctx.putImageData(new ImageData(this._model.toRGBA(), cv.width, cv.height), 0, 0)
    if (this._d && !this._erasePainting) {
      ctx.lineWidth = Math.max(1.5, cv.width / 300)
      ctx.strokeStyle = getComputedStyle(this).getPropertyValue('--tl4k-error').trim()
      ctx.stroke(new Path2D(this._d))
    }
  }

  _toImageCoords (e) {
    const rect = this.$preview.getBoundingClientRect()
    return {
      x: Math.floor((e.clientX - rect.left) * this.$preview.width / rect.width),
      y: Math.floor((e.clientY - rect.top) * this.$preview.height / rect.height)
    }
  }

  _showCursor (e) {
    if (!this.$eraseEnable.checked || this.$tool.value !== 'eraser') {
      this.$eraserCursor.style.display = 'none'
      return
    }
    const rect = this.$preview.getBoundingClientRect()
    const size = Number(this.$brush.value)
    this.$eraserCursor.style.width = `${size * rect.width / this.$preview.width}px`
    this.$eraserCursor.style.height = `${size * rect.height / this.$preview.height}px`
    this.$eraserCursor.style.left = `${e.clientX - rect.left}px`
    this.$eraserCursor.style.top = `${e.clientY - rect.top}px`
    this.$eraserCursor.style.display = 'block'
  }

  _paintErase (x, y) {
    if (!this._model || !this._erasePainting) return
    const last = this._lastPoint ?? { x, y }
    this._model.eraseLine(last.x, last.y, x, y, Number(this.$brush.value))
    this._lastPoint = { x, y }
    this._pixelsChanged()
  }

  _finishStroke () {
    if (!this._erasePainting) return
    this._erasePainting = false
    this._model?.finishStroke()
    if (this.$preview.hasPointerCapture(this._pointerId)) this.$preview.releasePointerCapture(this._pointerId)
    this._pointerId = null
    this._lastPoint = null
    this._syncControls()
    this._scheduleUpdate()
  }

  _onOk () {
    this._finishStroke()
    this._cancelUpdates()
    if (!this._update()) return
    if (!this._d && !(this._source === 'image' && this._model.hasErased)) {
      this.$hint.textContent = this._msgNoOutline ?? ''
      return
    }
    let png = null
    if (this._source === 'image' && this._model.hasErased) {
      try {
        const cv = document.createElement('canvas')
        cv.width = this._image.naturalWidth
        cv.height = this._image.naturalHeight
        cv.getContext('2d').putImageData(new ImageData(this._model.toRGBA(), cv.width, cv.height), 0, 0)
        png = cv.toDataURL('image/png')
        if (!png.startsWith('data:image/png;base64,')) throw new Error('PNG encoding failed')
      } catch (err) {
        this.reportError('encode_error', err)
        return
      }
    }
    const accepted = this.dispatchEvent(new CustomEvent('change', {
      cancelable: true,
      detail: {
        trigger: 'ok',
        source: this._source,
        sourceElement: this._sourceElement,
        sourceHref: this._sourceHref,
        png,
        d: this._d,
        naturalW: this._image.naturalWidth,
        naturalH: this._image.naturalHeight,
        dialog: 'closed'
      }
    }))
    if (accepted) this.dialog = 'close'
  }

  disconnectedCallback () {
    this._finishStroke()
    this._cancelUpdates()
    this._loadId++
  }
}

customElements.define('se-sticker-dialog', SeStickerDialog)
